'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

interface TicketForm {
  name: string;
  email: string;
  company: string;
  issue: string;
  budget: string;
}

const SESSION_KEY = 'mia_chat_session';
const MAX_MESSAGES = 50;

const SUGGESTED_PROMPTS = [
  'Help with LinkedIn Ads',
  'Google Ads best practices',
  'Meta Ads targeting tips',
  'TikTok Ads for beginners',
  'How does Incade work?',
  'Pricing & plans',
];

function getStoredMessages(): Message[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function storeMessages(messages: Message[]) {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(messages));
  } catch {
    // ignore
  }
}

function TypingIndicator() {
  return (
    <div className="flex items-end gap-2 mb-3">
      <div className="w-7 h-7 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 text-white text-xs font-bold">
        M
      </div>
      <div className="bg-slate-100 rounded-2xl rounded-bl-sm px-4 py-3">
        <div className="flex gap-1 items-center h-4">
          <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  );
}

export default function MiaChatWidget() {
  const { user, profile } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [ticketStep, setTicketStep] = useState<'idle' | 'collecting' | 'submitted'>('idle');
  const [ticketForm, setTicketForm] = useState<TicketForm>({ name: '', email: '', company: '', issue: '', budget: '' });
  const [ticketField, setTicketField] = useState<keyof TicketForm>('name');
  const [ticketSubmitting, setTicketSubmitting] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const TICKET_FIELDS: Array<{ key: keyof TicketForm; label: string; placeholder: string; required: boolean }> = [
    { key: 'name', label: 'Your name', placeholder: 'e.g. Jane Smith', required: true },
    { key: 'email', label: 'Your email', placeholder: 'e.g. jane@company.com', required: true },
    { key: 'company', label: 'Company (optional)', placeholder: 'e.g. Acme Corp', required: false },
    { key: 'issue', label: 'Describe your issue or request', placeholder: 'Tell us what you need help with...', required: true },
    { key: 'budget', label: 'Monthly ad budget (optional)', placeholder: 'e.g. $5,000/month', required: false },
  ];

  // Load messages from sessionStorage on mount
  useEffect(() => {
    const stored = getStoredMessages();
    setMessages(stored);
  }, []);

  // Persist messages to sessionStorage
  useEffect(() => {
    if (messages.length > 0) {
      storeMessages(messages);
    }
  }, [messages]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading, isOpen, ticketStep]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const addAssistantMessage = useCallback((content: string) => {
    const msg: Message = { role: 'assistant', content, timestamp: Date.now() };
    setMessages(prev => {
      const updated = [...prev, msg].slice(-MAX_MESSAGES);
      storeMessages(updated);
      return updated;
    });
  }, []);

  const getOpeningMessage = useCallback((): string => {
    if (!user) {
      return "Hi! I'm Mia, Incade's AI assistant. I specialize in digital marketing — LinkedIn, Google, Meta, TikTok, and Snapchat Ads. What can I help you with today?";
    }
    const firstName = profile?.first_name || profile?.full_name?.split(' ')[0] || 'there';
    return `Hi ${firstName}! How can I help you today?`;
  }, [user, profile]);

  const sendOpeningMessage = useCallback(async () => {
    if (!user) {
      const openingMsg: Message = {
        role: 'assistant',
        content: "Hi! I'm Mia, Incade's AI assistant. I specialize in digital marketing — LinkedIn, Google, Meta, TikTok, and Snapchat Ads. What can I help you with today?",
        timestamp: Date.now(),
      };
      setMessages([openingMsg]);
      storeMessages([openingMsg]);
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch('/api/mia/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [{ role: 'user', content: '__opening__' }],
          userId: user.id,
        }),
      });
      const data = await res.json();
      const openingMsg: Message = {
        role: 'assistant',
        content: data.reply || getOpeningMessage(),
        timestamp: Date.now(),
      };
      setMessages([openingMsg]);
      storeMessages([openingMsg]);
    } catch {
      const openingMsg: Message = {
        role: 'assistant',
        content: getOpeningMessage(),
        timestamp: Date.now(),
      };
      setMessages([openingMsg]);
      storeMessages([openingMsg]);
    } finally {
      setIsLoading(false);
    }
  }, [user, profile, getOpeningMessage]);

  const handleOpen = () => {
    setIsOpen(true);
    if (!hasOpened) {
      setHasOpened(true);
      const stored = getStoredMessages();
      if (stored.length === 0) {
        sendOpeningMessage();
      }
    }
  };

  const startTicketFlow = useCallback(() => {
    setTicketStep('collecting');
    setTicketField('name');
    setTicketForm({ name: '', email: '', company: '', issue: '', budget: '' });
    addAssistantMessage("I'll create a support ticket for you. Let's collect a few details.\n\nWhat's your name?");
  }, [addAssistantMessage]);

  const handleTicketInput = async (value: string) => {
    if (!value.trim() && TICKET_FIELDS.find(f => f.key === ticketField)?.required) return;

    const userMsg: Message = { role: 'user', content: value || '(skipped)', timestamp: Date.now() };
    setMessages(prev => {
      const updated = [...prev, userMsg].slice(-MAX_MESSAGES);
      storeMessages(updated);
      return updated;
    });

    const updatedForm = { ...ticketForm, [ticketField]: value };
    setTicketForm(updatedForm);
    setInput('');

    const currentIndex = TICKET_FIELDS.findIndex(f => f.key === ticketField);
    const nextField = TICKET_FIELDS[currentIndex + 1];

    if (nextField) {
      setTicketField(nextField.key);
      setTimeout(() => addAssistantMessage(nextField.label + (nextField.required ? '' : ' (type "skip" to skip)')), 300);
    } else {
      // Submit ticket
      setTicketSubmitting(true);
      try {
        const res = await fetch('/api/mia/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            messages: [],
            userId: user?.id,
            createTicket: updatedForm,
          }),
        });
        const data = await res.json();
        if (data.ticketCreated) {
          setTicketStep('submitted');
          addAssistantMessage("✅ Your support ticket has been submitted! Our team will reach out to you at " + updatedForm.email + " within 1 business day.");
        } else {
          addAssistantMessage("There was an issue submitting your ticket. Please try again or email us at support@incade.io");
          setTicketStep('idle');
        }
      } catch {
        addAssistantMessage("There was an issue submitting your ticket. Please try again or email us at support@incade.io");
        setTicketStep('idle');
      } finally {
        setTicketSubmitting(false);
      }
    }
  };

  const sendMessage = async (overrideText?: string) => {
    const text = (overrideText ?? input).trim();
    if (!text || isLoading) return;

    if (ticketStep === 'collecting') {
      await handleTicketInput(text === 'skip' ? '' : text);
      return;
    }

    const userMsg: Message = { role: 'user', content: text, timestamp: Date.now() };
    const newMessages = [...messages, userMsg].slice(-MAX_MESSAGES);
    setMessages(newMessages);
    storeMessages(newMessages);
    setInput('');
    setIsLoading(true);

    try {
      // Build conversation history for API (exclude opening placeholder)
      const apiMessages = newMessages
        .filter(m => m.content !== '__opening__')
        .map(m => ({ role: m.role, content: m.content }));

      const res = await fetch('/api/mia/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: apiMessages,
          userId: user?.id,
        }),
      });

      if (res.status === 429) {
        const errorMsg: Message = {
          role: 'assistant',
          content: "You've sent a lot of messages recently. Please wait a moment before sending more.",
          timestamp: Date.now(),
        };
        setMessages(prev => [...prev, errorMsg].slice(-MAX_MESSAGES));
        return;
      }

      const data = await res.json();

      if (data.requestTicket) {
        startTicketFlow();
        return;
      }

      const assistantMsg: Message = {
        role: 'assistant',
        content: data.reply || "I'm sorry, I couldn't process that. Please try again.",
        timestamp: Date.now(),
      };
      const updatedMessages = [...newMessages, assistantMsg].slice(-MAX_MESSAGES);
      setMessages(updatedMessages);
      storeMessages(updatedMessages);
    } catch {
      const errorMsg: Message = {
        role: 'assistant',
        content: "I'm having trouble connecting right now. Please try again in a moment.",
        timestamp: Date.now(),
      };
      const updatedMessages = [...newMessages, errorMsg].slice(-MAX_MESSAGES);
      setMessages(updatedMessages);
      storeMessages(updatedMessages);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const currentTicketField = TICKET_FIELDS.find(f => f.key === ticketField);
  const showSuggestedPrompts = messages.length <= 1 && ticketStep === 'idle' && !isLoading;

  return (
    <>
      {/* Floating button — bottom LEFT */}
      {!isOpen && (
        <button
          onClick={handleOpen}
          className="fixed bottom-6 left-6 z-50 flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white rounded-full px-4 py-3 shadow-lg transition-all duration-200 hover:scale-105 active:scale-95"
          aria-label="Open Mia chat assistant"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          <span className="text-sm font-semibold">Mia</span>
        </button>
      )}

      {/* Chat panel — bottom LEFT */}
      {isOpen && (
        <div
          className="fixed bottom-6 left-6 z-50 flex flex-col bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
          style={{ width: 380, height: 540, maxWidth: 'calc(100vw - 24px)', maxHeight: 'calc(100vh - 80px)' }}
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-slate-100 flex-shrink-0">
            <div className="relative">
              <div className="w-9 h-9 rounded-full bg-orange-500 flex items-center justify-center text-white text-sm font-bold">
                M
              </div>
              <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-400 rounded-full border-2 border-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#1E293B]">Mia</p>
              <p className="text-xs text-slate-500">Incade AI Assistant · Powered by Gemini</p>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setShowPrivacy(p => !p)}
                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                aria-label="Privacy info"
                title="Privacy notice"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                </svg>
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                aria-label="Close chat"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Privacy notice */}
          {showPrivacy && (
            <div className="bg-blue-50 border-b border-blue-100 px-4 py-3 flex-shrink-0">
              <p className="text-xs text-blue-700 leading-relaxed">
                <strong>Privacy Notice:</strong> This chat is powered by Google Gemini AI. Messages are processed to generate responses and may be reviewed to improve our service. We do not store sensitive personal data beyond this session. By chatting, you agree to our{' '}
                <a href="/privacy-policy" className="underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
              </p>
              <button onClick={() => setShowPrivacy(false)} className="mt-1.5 text-xs text-blue-600 underline">Dismiss</button>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex items-end gap-2 mb-3 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
              >
                {msg.role === 'assistant' && (
                  <div className="w-7 h-7 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 text-white text-xs font-bold">
                    M
                  </div>
                )}
                <div
                  className={`max-w-[75%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    msg.role === 'user' ?'bg-orange-500 text-white rounded-br-sm' :'bg-slate-100 text-[#1E293B] rounded-bl-sm'
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}

            {isLoading && <TypingIndicator />}

            {/* Suggested prompts */}
            {showSuggestedPrompts && (
              <div className="mt-3 flex flex-wrap gap-2">
                {SUGGESTED_PROMPTS.map((prompt) => (
                  <button
                    key={prompt}
                    onClick={() => sendMessage(prompt)}
                    className="text-xs px-3 py-1.5 rounded-full border border-orange-200 text-orange-600 hover:bg-orange-50 transition-colors"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            )}

            {/* Ticket CTA (after normal conversation) */}
            {ticketStep === 'idle' && messages.length > 2 && !isLoading && (
              <div className="mt-2 text-center">
                <button
                  onClick={startTicketFlow}
                  className="text-xs text-slate-400 hover:text-orange-500 transition-colors underline"
                >
                  Need more help? Create a support ticket
                </button>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="flex items-center gap-2 px-3 py-3 border-t border-slate-100 bg-white flex-shrink-0">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                ticketStep === 'collecting' ? currentTicketField?.placeholder ||'Type your answer...' :'Ask Mia anything...'
              }
              disabled={isLoading || ticketSubmitting || ticketStep === 'submitted'}
              className="flex-1 text-sm px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent bg-slate-50 placeholder-slate-400 text-[#1E293B] disabled:opacity-60"
            />
            <button
              onClick={() => sendMessage()}
              disabled={isLoading || ticketSubmitting || !input.trim() || ticketStep === 'submitted'}
              className="w-9 h-9 rounded-xl bg-orange-500 hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center justify-center transition-colors flex-shrink-0"
              aria-label="Send message"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.269 20.876L5.999 12zm0 0h7.5" />
              </svg>
            </button>
          </div>

          {/* GDPR footer */}
          <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 flex-shrink-0">
            <p className="text-[10px] text-slate-400 text-center">
              AI responses may not always be accurate.{' '}
              <button onClick={() => setShowPrivacy(true)} className="underline hover:text-slate-600">Privacy notice</button>
            </p>
          </div>
        </div>
      )}
    </>
  );
}
